/**
 * Member auth context — uses cookie-based JWT (set by backend's magic-link verify).
 * Distinct from AuthContext (which is for /admin).
 *
 * State semantics:
 *   member = undefined → still loading
 *   member = null      → not signed in
 *   member = object    → signed in
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

const MemberAuthContext = createContext(null);

export const useMember = () => useContext(MemberAuthContext);

const fetchMe = async () => {
  const { data } = await axios.get(`${API}/members/me`, {
    withCredentials: true,
  });
  return data;
};

export const MemberAuthProvider = ({ children }) => {
  const [member, setMember] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const me = await fetchMe();
      setMember(me);
      return me;
    } catch {
      setMember(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const requestMagicLink = useCallback(async (email) => {
    const { data } = await axios.post(
      `${API}/members/auth/request`,
      { email },
      { withCredentials: true }
    );
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(
        `${API}/members/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch {
      /* ignore */
    }
    setMember(null);
  }, []);

  const value = useMemo(
    () => ({ member, refresh, requestMagicLink, logout }),
    [member, refresh, requestMagicLink, logout]
  );

  return (
    <MemberAuthContext.Provider value={value}>
      {children}
    </MemberAuthContext.Provider>
  );
};
